/**
 * Super-admin per-tenant budget panel (v5 Phase 49).
 *
 * Shows the current month spend, monthly budget, percentage, alarm
 * threshold, and last-aggregated timestamp. Edit form lets the
 * super-admin update the monthly budget (USD) and alarm threshold
 * (50–95%). The spend column is read-only — it's materialised by the
 * cost aggregator every 5 minutes (Phase 46).
 */
import { useEffect, useState } from "react";
import { getTenantBudget, updateTenantBudget } from "../../services/adminTenants.js";

function formatUsd(value) {
  if (value === null || value === undefined) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return "—";
  return `$${n.toFixed(2)}`;
}

function formatPercent(spend, budget) {
  if (!spend || !budget || Number(budget) === 0) return "—";
  return `${((Number(spend) / Number(budget)) * 100).toFixed(1)}%`;
}

export default function BudgetPanel({ tenantId }) {
  const [budget, setBudget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ monthlyBudgetUsd: "", alarmThresholdPercent: "" });

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getTenantBudget(tenantId)
      .then((data) => {
        if (cancelled) return;
        setBudget(data);
        setForm({
          monthlyBudgetUsd: data?.monthlyBudgetUsd ?? "",
          alarmThresholdPercent: data?.alarmThresholdPercent ?? 80,
        });
        setError(null);
      })
      .catch((err) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = {};
      const m = parseFloat(form.monthlyBudgetUsd);
      const t = parseInt(form.alarmThresholdPercent, 10);
      if (Number.isFinite(m) && m > 0) payload.monthlyBudgetUsd = m;
      if (Number.isFinite(t) && t >= 50 && t <= 95) payload.alarmThresholdPercent = t;
      if (Object.keys(payload).length === 0) {
        setError("Provide a budget > 0 or threshold between 50 and 95");
        setSaving(false);
        return;
      }
      const updated = await updateTenantBudget(tenantId, payload);
      setBudget(updated);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Loading budget…</p>;

  return (
    <section className="budget-panel">
      <h2>Budget</h2>
      {error && <p className="error">{error}</p>}
      {budget?.missing ? (
        <p className="muted">
          No budget row exists for this tenant. The provisioning state machine
          writes one on tenant creation; if this tenant predates Phase 45, run
          the backfill.
        </p>
      ) : (
        <dl className="detail-grid">
          <dt>Current month spend</dt>
          <dd>{formatUsd(budget?.currentMonthSpendUsd)}</dd>
          <dt>Monthly budget</dt>
          <dd>{formatUsd(budget?.monthlyBudgetUsd)}</dd>
          <dt>Spend %</dt>
          <dd>
            {formatPercent(budget?.currentMonthSpendUsd, budget?.monthlyBudgetUsd)}
          </dd>
          <dt>Alarm threshold</dt>
          <dd>{budget?.alarmThresholdPercent ?? "—"}%</dd>
          <dt>Last aggregated</dt>
          <dd>{budget?.lastAggregatedAt || "—"}</dd>
          <dt>Updated by</dt>
          <dd>{budget?.updatedBy || "—"}</dd>
        </dl>
      )}

      {!editing && (
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setEditing(true)}
        >
          Edit budget
        </button>
      )}

      {editing && (
        <form onSubmit={onSubmit} className="budget-form">
          <label>
            Monthly budget (USD)
            <input
              type="number"
              min="1"
              step="1"
              value={form.monthlyBudgetUsd}
              onChange={(e) =>
                setForm({ ...form, monthlyBudgetUsd: e.target.value })
              }
            />
          </label>
          <label>
            Alarm threshold (%)
            <input
              type="number"
              min="50"
              max="95"
              step="1"
              value={form.alarmThresholdPercent}
              onChange={(e) =>
                setForm({ ...form, alarmThresholdPercent: e.target.value })
              }
            />
          </label>
          <div className="budget-form__actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
